import { useCallback, useEffect } from 'react';

import {
  ENTERPRISE_CONTRIBUTOR_LIMIT_DEFAULT,
  ENTERPRISE_RELEASE_GROUP_LIMIT_DEFAULT,
} from '../../../shared/subscriptions/consts';
import * as api from '../../redux/api';
import useAsyncRequest from '../useAsyncRequest';
import useEditable from '../useEditable';
import type { OrganizationContract } from '../../../shared/model-typings/organizationcontract';
import type { FossaSubscriptionPlan } from '../../types';

// Define the type for the contract properties
export type ContractProps = Pick<
  OrganizationContract,
  'releaseGroupLimit' | 'contributorLimit' | 'unlimitedContributors' | 'unlimitedReleaseGroups'
>;

export type UpdateContractPropertyFunction = (property: keyof ContractProps, value: unknown) => void;

export type ContractPropsResult = {
  contractProps: ContractProps;
  updateContractProperty: UpdateContractPropertyFunction;
  isLoading: boolean;
  errors: string[] | null;
  isDirty: boolean;
  onSave: () => void;
  onReset: () => void;
};

const initialDataState = {
  releaseGroupLimit: ENTERPRISE_RELEASE_GROUP_LIMIT_DEFAULT,
  contributorLimit: ENTERPRISE_CONTRIBUTOR_LIMIT_DEFAULT,
  unlimitedContributors: false,
  unlimitedReleaseGroups: false,
};

// Define the custom hook
/**
 * A custom hook that manages contract properties and provides methods to update them.
 *
 * @returns {ContractPropsResult} - An object containing the current contract properties and an updateProperty method,.
 */
function useCurrentOrgContractProps(): ContractPropsResult {
  const { data, isDirty, onUpdate, onSave, onReset } = useEditable<ContractProps>(initialDataState);
  const request = useCallback(() => {
    return api.get<FossaSubscriptionPlan>(`/api/organization/subscription`);
  }, []);
  const asyncValue = useAsyncRequest(request);

  useEffect(() => {
    if (!asyncValue.value) {
      return;
    }
    onUpdate({
      ...initialDataState,
      ...asyncValue.value.contract,
    });
  }, [asyncValue.value, onUpdate]);

  // Define a function to update a specific property
  const updateContractProperty = useCallback(
    (property: keyof ContractProps, value: unknown) => {
      onUpdate({ ...data, [property]: value });
    },
    [onUpdate, data],
  );

  return {
    contractProps: data,
    updateContractProperty,
    isLoading: asyncValue.isLoading,
    errors: asyncValue.errors,
    isDirty,
    onSave,
    onReset,
  };
}

export default useCurrentOrgContractProps;
